import type { startseite } from "@progressive-development/politik-model";

import { ROUTE_WAHLEN } from "../../config/navigation.js";
import { artKenntOrt, teilwahlRang } from "./wahl-arten.js";

/**
 * Die Adresse einer Wahl-Ansicht — Adresse ↔ Auswahl, an EINER Stelle.
 *
 * Der Auswahlraum hat drei Achsen (Fachkonzept 1): Art, Zeitraum (die Wahl)
 * und Ort. Die Detailseite trägt alle drei in der Adresse, weil ein geteilter
 * Link bei jedem dasselbe zeigen muss (2.2):
 *
 *   /wahlen/kw2025/rat                              Stadt
 *   /wahlen/kw2025/rat/bezirk/ehrenfeld
 *   /wahlen/kw2025/rat/veedel/neuehrenfeld
 *   /wahlen/kw2025/bezirksvertretung/ehrenfeld      der Bezirk ist Teil der Teilwahl
 *   /wahlen/kw2025/bezirksvertretung/ehrenfeld/veedel/bickendorf
 *
 * Die Slugs kommen aus den Daten: `WahlEintrag.slug` (Pipeline, E1), der
 * `art`-Slug der Teilwahl, die Gebiets-Slugs. Hier wird nichts aus Namen
 * gebildet und nichts aus Kürzeln abgeleitet.
 *
 * Bei der Bezirksvertretung steht der Bezirk direkt hinter der Art — es gibt
 * keine BV-Wahl der Stadt, und `bezirk/` als Segment wäre dieselbe
 * Information zweimal.
 */

export const ART_BEZIRKSVERTRETUNG = "bezirksvertretung";

export interface WahlAuswahl {
  /** Slug der Wahl, etwa "kw2025". */
  wahl: string;
  /** Art der Teilwahl, etwa "rat". */
  art: string;
  /** Bezirks-Slug — Ort (Rat, OB, Stichwahl) oder Teil der Teilwahl (BV). */
  bezirk?: string;
  /** Stadtteil-Slug — die feinste Ebene des Kapitels. */
  stadtteil?: string;
}

/** Der Weg auf die Übersicht — mit Wahl: das Jahr aufgeklappt (3.4). */
export function uebersichtPfad(wahlSlug?: string): string {
  return wahlSlug ? `/${ROUTE_WAHLEN}/${wahlSlug}` : `/${ROUTE_WAHLEN}`;
}

/**
 * Der Weg auf die Detailseite.
 *
 * Ohne Ort ist die Stadt gemeint; der Stadtteil schlägt den Bezirk als Ort,
 * weil er der feinere ist (bei der BV bleibt der Bezirk, weil er zur
 * Teilwahl gehört). Eine Bezirksvertretung ohne Bezirk hat keine Adresse —
 * dann führt der Weg auf die Übersicht, wo die neun Bezirke stehen (5.3).
 */
export function wahlPfad(a: WahlAuswahl): string {
  const basis = `/${ROUTE_WAHLEN}/${a.wahl}/${a.art}`;
  if (a.art === ART_BEZIRKSVERTRETUNG) {
    if (!a.bezirk) return uebersichtPfad(a.wahl);
    const bv = `${basis}/${a.bezirk}`;
    return a.stadtteil ? `${bv}/veedel/${a.stadtteil}` : bv;
  }
  if (a.stadtteil) return `${basis}/veedel/${a.stadtteil}`;
  if (a.bezirk) return `${basis}/bezirk/${a.bezirk}`;
  return basis;
}

/**
 * Die Umkehrung: aus den Routen-Parametern die Auswahl.
 *
 * Die BV-Muster liefern keinen `art`-Parameter (die Art steht als festes
 * Segment in der Adresse) — dann ist sie die Bezirksvertretung. Ohne Wahl
 * oder ohne Art gibt es keine Auswahl; die Seite zeigt dann ihren
 * Leerzustand statt zu raten.
 */
export function auswahlAusRoute(
  params: Record<string, string | undefined> | undefined,
): WahlAuswahl | undefined {
  const wahl = params?.wahl;
  const art = params?.art ?? (params?.bezirk !== undefined ? ART_BEZIRKSVERTRETUNG : undefined);
  if (!wahl || !art) return undefined;
  return {
    wahl,
    art,
    bezirk: params?.bezirk || undefined,
    stadtteil: params?.stadtteil || undefined,
  };
}

/**
 * Den Bezirk aus dem Stadtteil ergänzen.
 *
 * Die Adresse eines Stadtteils nennt den Bezirk NICHT (`…/rat/veedel/nippes`) —
 * sie muss es auch nicht, ein Stadtteil liegt in genau einem Bezirk. Die
 * Auswahlleiste braucht ihn aber: Ohne ihn stünde das Bezirksfeld auf „ganze
 * Stadt", und die Stadtteil-Liste (die am Bezirk hängt) wäre leer — der Ort
 * ginge beim Blick auf die Leiste verloren, obwohl die Seite ihn zeigt.
 *
 * Ergänzt wird über die EBENE, nie über den Slug: acht der neun Bezirks-Slugs
 * sind zugleich Stadtteil-Slugs.
 */
export function mitBezirk(
  a: WahlAuswahl | undefined,
  gebiete: readonly startseite.GebietEintrag[] | undefined,
): WahlAuswahl | undefined {
  if (!a || a.bezirk || !a.stadtteil) return a;
  const stadtteil = gebiete?.find((g) => g.ebene === "stadtteil" && g.slug === a.stadtteil);
  return stadtteil?.bezirkSlug ? { ...a, bezirk: stadtteil.bezirkSlug } : a;
}

/**
 * Der Ort eines Gebiets als Auswahl — über die EBENE, nie über den Slug:
 * acht der neun Bezirks-Slugs sind zugleich Stadtteil-Slugs.
 */
export function ortVon(
  gebiet: startseite.GebietEintrag | undefined,
): Pick<WahlAuswahl, "bezirk" | "stadtteil"> {
  if (!gebiet) return {};
  if (gebiet.ebene === "stadtbezirk") return { bezirk: gebiet.slug };
  if (gebiet.ebene === "stadtteil") return { bezirk: gebiet.bezirkSlug, stadtteil: gebiet.slug };
  return {};
}

/** Der Wahl-Eintrag zu einem Kürzel („KW2025") — Schlüssel, nicht Name. */
export function wahlEintragVon(
  wahlen: readonly startseite.WahlEintrag[] | undefined,
  kuerzel: string | undefined,
): startseite.WahlEintrag | undefined {
  return kuerzel ? wahlen?.find((w) => w.id === kuerzel) : undefined;
}

/** Der Wahl-Eintrag zu einem Slug („kw2025") — für die Adresse. */
export function wahlEintragZuSlug(
  wahlen: readonly startseite.WahlEintrag[] | undefined,
  slug: string | undefined,
): startseite.WahlEintrag | undefined {
  return slug ? wahlen?.find((w) => w.slug === slug) : undefined;
}

/**
 * Die Wahlen des Bestands, neueste zuerst — nach dem letzten Wahltag, nicht
 * nach dem Kürzel (`EUW2019` sortierte alphabetisch hinter `BTW2021`).
 * Einträge ohne Tag stehen hinten, in der Reihenfolge der Quelle.
 */
export function wahlenNeuesteZuerst(
  wahlen: readonly startseite.WahlEintrag[] | undefined,
): startseite.WahlEintrag[] {
  return [...(wahlen ?? [])].sort((a, b) => (b.letzterTag ?? "").localeCompare(a.letzterTag ?? ""));
}

/**
 * Die unmittelbar vorhergehende Wahl — genau eine (1.7: kein Verlauf).
 *
 * Entschieden über den Wahltag, nicht über das Kürzel. Fehlt der Tag, gibt
 * es keine Antwort: Eine Reihenfolge zu raten hieße, eine zu behaupten.
 */
export function vorigeWahl(
  wahlen: readonly startseite.WahlEintrag[] | undefined,
  kuerzel: string | undefined,
  art?: string,
): startseite.WahlEintrag | undefined {
  const aktuelle = wahlEintragVon(wahlen, kuerzel);
  if (!aktuelle?.letzterTag) return undefined;
  const sortiert = wahlenNeuesteZuerst(wahlen)
    .filter((w) => w.letzterTag)
    /*
     * MIT ART: die vorige Wahl DIESER Art, nicht die im Bestand nächstältere.
     * Seit Bundestag und Europa im Bestand sind, liegt zwischen zwei
     * Kommunalwahlen anderes — der Fußlink „vorige Wahl ansehen" führte von
     * der Ratswahl 2025 zur Bundestagswahl 2025, mit `art=rat` also ins Leere.
     */
    .filter((w) => !art || w.teilwahlen.some((t) => t.art === art));
  const i = sortiert.findIndex((w) => w.id === aktuelle.id);
  return i >= 0 ? sortiert[i + 1] : undefined;
}

// -----------------------------------------------------------------------------
// Von der Auswahl zum Dokument — und zu den möglichen Wechseln
// -----------------------------------------------------------------------------

/** Die Teilwahl, die eine Auswahl in einer Wahl meint. */
function teilwahlDerAuswahl(
  w: startseite.WahlEintrag | undefined,
  a: Pick<WahlAuswahl, "art" | "bezirk">,
): startseite.WahlEintragTeilwahl | undefined {
  return w?.teilwahlen.find(
    (t) => t.art === a.art && (a.art !== ART_BEZIRKSVERTRETUNG || t.bezirk === a.bezirk),
  );
}

/** Das Gebiet einer Auswahl — über Ebene und Slug, wie überall. */
export function gebietDerAuswahl(
  gebiete: readonly startseite.GebietEintrag[] | undefined,
  a: Pick<WahlAuswahl, "bezirk" | "stadtteil">,
): startseite.GebietEintrag | undefined {
  if (a.stadtteil) return gebiete?.find((g) => g.ebene === "stadtteil" && g.slug === a.stadtteil);
  if (a.bezirk) return gebiete?.find((g) => g.ebene === "stadtbezirk" && g.slug === a.bezirk);
  return gebiete?.find((g) => g.ebene === "gesamtstadt");
}

/** Was eine Sicht-ID ausmacht — beide Schlüssel, aus den Verzeichnissen. */
export interface SichtSchluessel {
  id: string;
  teilwahlId: string;
  gebietId: string;
  wahl: startseite.WahlEintrag;
  teilwahl: startseite.WahlEintragTeilwahl;
  gebiet: startseite.GebietEintrag;
}

/**
 * Die Dokument-ID der Ergebnis-Sicht zu einer Auswahl: `{teilwahlId}:{gebietId}`.
 *
 * Beide Hälften kommen aus den Verzeichnissen — der Teilwahl-Schlüssel aus
 * `wahlen`, der Gebietsschlüssel aus Ebene und Nummer des Gebiets. Nichts
 * wird aus der Adresse zusammengeschnitten. Fehlt eine Hälfte (unbekannte
 * Wahl, Art ohne Teilwahl in dieser Wahl, unbekanntes Gebiet), gibt es keine
 * ID — und die Seite ihren Leerzustand.
 */
export function sichtId(
  a: WahlAuswahl | undefined,
  wahlen: readonly startseite.WahlEintrag[] | undefined,
  gebiete: readonly startseite.GebietEintrag[] | undefined,
): SichtSchluessel | undefined {
  if (!a) return undefined;
  const wahl = wahlEintragZuSlug(wahlen, a.wahl);
  const teilwahl = teilwahlDerAuswahl(wahl, a);
  const gebiet = gebietDerAuswahl(gebiete, a);
  if (!wahl || !teilwahl?.teilwahlId || !gebiet?.ebene || !gebiet.nr) return undefined;
  const gebietId = `${gebiet.ebene}:${gebiet.nr}`;
  return {
    id: `${teilwahl.teilwahlId}:${gebietId}`,
    teilwahlId: teilwahl.teilwahlId,
    gebietId,
    wahl,
    teilwahl,
    gebiet,
  };
}

/** Ein Wechsel auf der Auswahlleiste: Schlüssel, und der Weg dorthin. */
export interface Wechsel {
  schluessel: string;
  pfad: string;
}

/** Die möglichen Wechsel je Achse — nur, was ein Ziel hat (Fachkonzept 2.3 B). */
export interface MoeglicheWechsel {
  arten: Wechsel[];
  wahlen: Wechsel[];
  /** Die Stadt als Ort — fehlt bei der Bezirksvertretung (es gibt keine der Stadt). */
  stadt?: Wechsel;
  bezirke: Wechsel[];
  /** Die Stadtteile des gewählten Bezirks; leer ohne Bezirk. */
  stadtteile: Wechsel[];
}

/**
 * Die jüngste Wahl, die eine Art führt — bei der Bezirksvertretung die, die
 * sie für DIESEN Bezirk führt. Über den Wahltag der Teilwahl, nie über den
 * Namen der Wahl.
 */
function juengsteWahlMitArt(
  wahlen: readonly startseite.WahlEintrag[] | undefined,
  art: string,
  bezirk: string | undefined,
): startseite.WahlEintrag | undefined {
  let beste: { wahl: startseite.WahlEintrag; datum: string } | undefined;
  for (const w of wahlen ?? []) {
    for (const t of w.teilwahlen) {
      if (t.art !== art) continue;
      if (art === ART_BEZIRKSVERTRETUNG && bezirk && t.bezirk !== bezirk) continue;
      const datum = t.datum ?? "";
      if (!beste || datum > beste.datum) beste = { wahl: w, datum };
    }
  }
  return beste?.wahl;
}

/**
 * Was sich von einer Auswahl aus wechseln lässt — die anderen Achsen bleiben,
 * „wenn möglich": Eine Art, die es in dieser Wahl nicht gibt (keine Stichwahl
 * nötig), wird nicht angeboten; eine Wahl, die diese Art nicht hatte, auch
 * nicht. Bei der Bezirksvertretung bleibt der Bezirk Teil der Teilwahl —
 * wer den Bezirk wechselt, wechselt die Teilwahl.
 */
export function moeglicheWechsel(
  a: WahlAuswahl,
  wahlen: readonly startseite.WahlEintrag[] | undefined,
  gebiete: readonly startseite.GebietEintrag[] | undefined,
): MoeglicheWechsel {
  const wahl = wahlEintragZuSlug(wahlen, a.wahl);
  const bezirke = (gebiete ?? []).filter((g) => g.ebene === "stadtbezirk");
  const bv = a.art === ART_BEZIRKSVERTRETUNG;

  /*
   * DIE ARTEN GEHEN ÜBER DIE WAHL HINAUS.
   *
   * Vorher kamen sie aus den Teilwahlen DIESER Wahl — von der Ratswahl führte
   * also kein Weg zur Bundestagswahl, weil beide keine gemeinsame Art haben.
   * Jetzt stehen alle Arten des Bestands zur Wahl, und der Ort bleibt dabei
   * erhalten: sein Veedel artübergreifend durchklicken ist der Sinn der Sache.
   *
   * Welche Wahl eine Art trifft: DIESE, wenn sie die Art führt (wer 2020
   * ansieht und die Art wechselt, bleibt in 2020) — sonst die jüngste, die sie
   * hat. Die Bezirksvertretung braucht weiterhin einen Bezirk.
   */
  const alleArten = [
    ...new Set((wahlen ?? []).flatMap((w) => w.teilwahlen.map((t) => t.art).filter(Boolean))),
  ] as string[];
  const arten = alleArten
    .filter((art) => art !== ART_BEZIRKSVERTRETUNG || a.bezirk)
    .map((art) => {
      const hier = wahl?.teilwahlen.some((t) => t.art === art);
      const ziel = hier ? wahl : juengsteWahlMitArt(wahlen, art, a.bezirk);
      /*
       * DEN ORT NUR MITNEHMEN, WENN DIE ZIELART IHN KENNT. Die Landtagswahl
       * gibt es nur für die Stadt — wer aus Bickendorf dorthin wechselt, landete
       * sonst auf einer Adresse ohne Sicht und las „kein amtliches Ergebnis".
       * Dieselbe angebotene Sackgasse wie einst bei der Bezirksvertretung, nur
       * andersherum. Was eine Art kennt, steht in `wahl-arten.ts`.
       */
      const ort = {
        bezirk: artKenntOrt(art, "stadtbezirk") ? a.bezirk : undefined,
        stadtteil: artKenntOrt(art, "stadtteil") ? a.stadtteil : undefined,
      };
      return ziel
        ? { schluessel: art, pfad: wahlPfad({ ...a, ...ort, wahl: ziel.slug, art }) }
        : undefined;
    })
    .filter((w): w is Wechsel => w !== undefined)
    .sort((x, y) => teilwahlRang(x.schluessel) - teilwahlRang(y.schluessel));

  const wahlenMitArt = wahlenNeuesteZuerst(wahlen)
    .filter((w) => teilwahlDerAuswahl(w, a))
    .map((w) => ({ schluessel: w.slug, pfad: wahlPfad({ ...a, wahl: w.slug }) }));

  const bezirkeWechsel = (artKenntOrt(a.art, "stadtbezirk") ? bezirke : [])
    .filter((b) => !bv || teilwahlDerAuswahl(wahl, { art: a.art, bezirk: b.slug }))
    .map((b) => ({
      schluessel: b.slug,
      pfad: wahlPfad({ wahl: a.wahl, art: a.art, bezirk: b.slug }),
    }));

  /*
   * Stadtteile gibt es bei JEDER Art — auch bei der Bezirksvertretung: Seit
   * dem 28.08.2026 liefert die Pipeline die BV auch je Stadtteil (gerechnet
   * aus den Stimmbezirken, deren Nummer auf den Stadtteil lautet, Briefwahl
   * eingeschlossen; s. RUECKMELDUNG-bv-je-stadtteil.md). Fehlt eine Sicht
   * einmal, ist der Leerzustand der Seite die Antwort — die Pipeline liefert
   * lieber nichts als Ungeprüftes, und das ist kein Fehler der App.
   */
  const stadtteile = (artKenntOrt(a.art, "stadtteil") ? (gebiete ?? []) : [])
    .filter((g) => g.ebene === "stadtteil" && a.bezirk && g.bezirkSlug === a.bezirk)
    .sort((x, y) => x.name.localeCompare(y.name, "de"))
    .map((g) => ({
      schluessel: g.slug,
      pfad: wahlPfad({ wahl: a.wahl, art: a.art, bezirk: a.bezirk, stadtteil: g.slug }),
    }));

  return {
    arten,
    wahlen: wahlenMitArt,
    /*
     * Die Stadt als Ort — fehlt, wo es sie nicht gibt (Bezirksvertretung).
     * Auch das sagt jetzt die Tabelle, nicht mehr eine Bedingung auf die Art.
     */
    stadt: artKenntOrt(a.art, "gesamtstadt")
      ? { schluessel: "", pfad: wahlPfad({ wahl: a.wahl, art: a.art }) }
      : undefined,
    bezirke: bezirkeWechsel,
    stadtteile,
  };
}
